import * as React from 'react';

import type Moment from 'moment';

import {SelectPickerGeneric} from '../select-picker-generic/select-picker-generic';

import {
    getTimeOptions,
    getDurationText,
    isJudgedFromBaselineTime,
    roundTimeToNextQuarter,
    parseTimeString,
    isValidTimeString,
} from './helpers';

type Props = {
    // @ts-expect-error
    selectedTime: Moment;
    // @ts-expect-error
    onChange: (newTime: Moment) => void;
    maxTime?: string;
    minTime?: string;
    allowPastTimes?: boolean;
    minHeight?: number;
    // @ts-expect-error
    additionalTimes?: Moment[]; // For adding times that are not in the 15 minute increments,
    // @ts-expect-error
    baselineTime?: Moment; // For multi day time options based around a given start time,
    isDisabled?: boolean;
    isCreatable?: boolean; // Allow creating custom times,
    createOnBlur?: boolean;
    // @ts-expect-error
    dateForCreation?: Moment; // The date to use when creating a new time
};

export function SelectTimePicker(props: Props) {
    const {onChange} = props;

    const options = getTimeOptions(
        props.selectedTime,
        props.baselineTime,
        props.maxTime,
        props.minTime,
        props.allowPastTimes,
        props.additionalTimes
    );

    const roundedSelectedTime = roundTimeToNextQuarter(props.selectedTime);

    // First try to find an exact match in additional times
    const isExactMatch =
        props.additionalTimes &&
        props.additionalTimes.some((time) => time.isSame(props.selectedTime, 'minute'));

    // Find matching option, prioritizing exact matches over rounded time
    const selectedValue = options.find((option) =>
        isExactMatch
            ? option.value.isSame(props.selectedTime, 'minute')
            : option.value.isSame(roundedSelectedTime, 'minute')
    );

    const hasRightContent = isJudgedFromBaselineTime(props.baselineTime, props.selectedTime);

    const handleChange = React.useCallback(
        (selected) => {
            onChange(selected.value);
        },
        [onChange]
    );

    const handleCreate = React.useCallback(
        (inputValue: string) => {
            const parsedTime = parseTimeString(inputValue, props.dateForCreation);
            if (parsedTime) {
                onChange(parsedTime);
            }
        },
        [onChange, props.dateForCreation]
    );

    const formatCreateLabel = React.useCallback((inputValue) => {
        const parsedTime = parseTimeString(inputValue);
        if (parsedTime) {
            return parsedTime.format('h:mm A');
        }

        return inputValue;
    }, []);

    const isValidNewOption = React.useCallback(
        (inputValue: string) => {
            // First check if it's a valid time string
            if (!isValidTimeString(inputValue)) {
                return false;
            }

            // Parse the input to get the time
            const parsedTime = parseTimeString(inputValue);
            if (!parsedTime) {
                return false;
            }

            // Check if this time already exists in the options
            const timeAlreadyExists = options.some((option) =>
                option.value.isSame(parsedTime, 'minute')
            );

            // Only allow creation if the time doesn't already exist
            return !timeAlreadyExists;
        },
        [options]
    );

    return (
        <SelectPickerGeneric
            options={options}
            value={selectedValue}
            onChange={handleChange}
            minHeight={props.minHeight}
            isDisabled={props.isDisabled}
            // Warning: We are overloading this component to prevent a bigger
            // refactor. If you need creatable functionality, look to other
            // components first.
            isCreatable={props.isCreatable}
            onCreateOption={props.isCreatable ? handleCreate : undefined}
            isValidNewOption={props.isCreatable ? isValidNewOption : undefined}
            formatCreateLabel={props.isCreatable ? formatCreateLabel : undefined}
            rightContent={
                hasRightContent
                    ? {
                          // @ts-expect-error
                          getOptionContent: (option) => (
                              <div>{getDurationText(props.baselineTime, option.value)}</div>
                          ),
                      }
                    : undefined
            }
            menuWidth={hasRightContent ? 170 : undefined}
            createOnBlur={props.createOnBlur}
        />
    );
}
